import type { RectMm } from '../layout/doorLayout';
import {
  SOURCE_HEAD_CORNER,
  SOURCE_HEAD_OUTER_BAND,
  type ButtHeadSliceName,
  type FrameProfileDetailSliceName,
  type FrameProfileNineSliceContract,
  type FrameProfileRequiredSliceName,
} from '../assets/frameProfileSlices';
import type { FrameJointConstruction } from './frameProfileRenderSpec';

/** Bottom corners are fused with threshold/cill artwork and must not be painted. */
export const UNSUITABLE_LIVE_FRAME_SLICES: FrameProfileRequiredSliceName[] = [
  'bottomLeftCorner',
  'bottomRightCorner',
];

/** Stretched 1-D runs. Painted before corners so fused source mitres stay under fixed caps. */
export const LIVE_FRAME_RUN_NAMES = ['headRun', 'leftJambRun', 'rightJambRun'] as const;

export const LIVE_FRAME_CORNER_NAMES = ['topLeftCorner', 'topRightCorner'] as const;

/** Jambs paint first; the full-width head then masks their square upper ends. */
export const BUTT_FRAME_RUN_NAMES = ['leftJambRun', 'rightJambRun', 'headRun'] as const;

export function getFrameRunPaintOrder(joint: FrameJointConstruction) {
  return joint === 'butt' ? BUTT_FRAME_RUN_NAMES : LIVE_FRAME_RUN_NAMES;
}

export function getFrameCornerPaintOrder(joint: FrameJointConstruction) {
  return joint === 'butt' ? [] : LIVE_FRAME_CORNER_NAMES;
}

export const LIVE_FRAME_SLICE_NAMES = [
  ...LIVE_FRAME_RUN_NAMES,
  ...LIVE_FRAME_CORNER_NAMES,
] as const;

export interface FrameNineSliceDestinations {
  head: RectMm;
  leftJamb: RectMm;
  rightJamb: RectMm;
  slices: Record<(typeof LIVE_FRAME_SLICE_NAMES)[number], RectMm>;
  detailSlices: Record<FrameProfileDetailSliceName, RectMm>;
  buttHeadSlices: Record<ButtHeadSliceName, RectMm>;
}

function mapChildIntoParent(parentSource: RectMm, childSource: RectMm, parentDest: RectMm): RectMm {
  const scaleX = parentDest.width / parentSource.width;
  const scaleY = parentDest.height / parentSource.height;
  return {
    x: parentDest.x + (childSource.x - parentSource.x) * scaleX,
    y: parentDest.y + (childSource.y - parentSource.y) * scaleY,
    width: childSource.width * scaleX,
    height: childSource.height * scaleY,
  };
}

/**
 * Millimetre destinations for a U-shaped frame that surrounds `slabOpeningBounds`
 * and meets the bottom of `frameBounds` (cill top, or overall bottom).
 * When a threshold is present it sits between the jambs at slab width; jambs
 * continue down beside it rather than stopping at the threshold top.
 */
export function computeFrameNineSliceDestinations(
  frameBounds: RectMm,
  slabOpeningBounds: RectMm,
  contract: FrameProfileNineSliceContract,
  joint: FrameJointConstruction = 'mitred'
): FrameNineSliceDestinations {
  const headHeight = slabOpeningBounds.y - frameBounds.y;
  const leftWidth = slabOpeningBounds.x - frameBounds.x;
  const rightX = slabOpeningBounds.x + slabOpeningBounds.width;
  const rightWidth = frameBounds.x + frameBounds.width - rightX;
  const frameBottom = frameBounds.y + frameBounds.height;
  const jambTop = joint === 'butt' ? frameBounds.y : slabOpeningBounds.y;
  const jambHeight = frameBottom - jambTop;

  const head: RectMm = {
    x: frameBounds.x,
    y: frameBounds.y,
    width: frameBounds.width,
    height: headHeight,
  };
  const leftJamb: RectMm = {
    x: frameBounds.x,
    y: jambTop,
    width: leftWidth,
    height: jambHeight,
  };
  const rightJamb: RectMm = {
    x: rightX,
    y: jambTop,
    width: rightWidth,
    height: jambHeight,
  };

  const topLeftCorner: RectMm = {
    x: frameBounds.x,
    y: frameBounds.y,
    width: leftWidth,
    height: headHeight,
  };
  const topRightCorner: RectMm = {
    x: rightX,
    y: frameBounds.y,
    width: rightWidth,
    height: headHeight,
  };
  const headRun: RectMm = {
    x: slabOpeningBounds.x,
    y: frameBounds.y,
    width: slabOpeningBounds.width,
    height: headHeight,
  };
  const leftJambRun: RectMm = { ...leftJamb };
  const rightJambRun: RectMm = { ...rightJamb };
  const outerBandWidth = leftWidth * (SOURCE_HEAD_OUTER_BAND / SOURCE_HEAD_CORNER);
  const rightOuterBandWidth = rightWidth * (SOURCE_HEAD_OUTER_BAND / SOURCE_HEAD_CORNER);

  return {
    head,
    leftJamb,
    rightJamb,
    slices: {
      topLeftCorner,
      topRightCorner,
      headRun,
      leftJambRun,
      rightJambRun,
    },
    buttHeadSlices: {
      leftHeadEnd: topLeftCorner,
      rightHeadEnd: topRightCorner,
      leftHeadOuter: {
        x: frameBounds.x,
        y: frameBounds.y,
        width: outerBandWidth,
        height: headHeight,
      },
      rightHeadOuter: {
        x: frameBounds.x + frameBounds.width - rightOuterBandWidth,
        y: frameBounds.y,
        width: rightOuterBandWidth,
        height: headHeight,
      },
    },
    detailSlices: {
      headDetailRun: mapChildIntoParent(
        contract.slices.headRun.sourceBounds,
        contract.detailSlices.headDetailRun.sourceBounds,
        headRun
      ),
      leftJambDetailRun: mapChildIntoParent(
        contract.slices.leftJambRun.sourceBounds,
        contract.detailSlices.leftJambDetailRun.sourceBounds,
        leftJambRun
      ),
      rightJambDetailRun: mapChildIntoParent(
        contract.slices.rightJambRun.sourceBounds,
        contract.detailSlices.rightJambDetailRun.sourceBounds,
        rightJambRun
      ),
    },
  };
}
